@if (failed) {
  <cs-info-card type="warning" [dismissible]="false">
    {{ LL.bodyCompOverview.loadFailed() }}
    <cs-button tertiary (click)="retry.emit()">{{ LL.bodyCompOverview.retry() }}</cs-button>
  </cs-info-card>
} @else if (!model) {
  <!-- no model yet: a spinner only while a load is really running. A host that has not started
       loading (the check-up before its Start button) draws nothing, so the page does not look busy -->
  @if (loading) {
    <div class="overview-loading">
      <cs-spinner></cs-spinner>
    </div>
  }
} @else {
  <!-- one value drawn like a row of the Progress page's "Measurements" card (#6676): label, value,
       the change in brackets and the arrow in the series colour, then the per-week line -->
  <ng-template #measurementTemplate let-measurement>
    <div class="measurement" [style.--change-color]="'var(' + measurement.color + ')'">
      @if (measurement.label) {
        <div class="measurement-label">{{ measurement.label }}</div>
      }
      <div class="measurement-value">
        @if (measurement.valueText) {
          <span class="current-value">{{ measurement.valueText }}</span>
        }
        @if (measurement.changeText || measurement.trendIcon) {
          <span class="change-text">
            @if (measurement.changeText) {
              ({{ measurement.changeText }})
            }
            @if (measurement.trendIcon) {
              <cs-icon [name]="measurement.trendIcon"></cs-icon>
            }
          </span>
        }
      </div>
      @if (measurement.perWeek; as perWeek) {
        <div class="measurement-change">
          <span class="change-value">{{ perWeek.lead }}</span>
          <span class="change-label">{{ perWeek.rest }}</span>
        </div>
      }
    </div>
  </ng-template>

  <div class="overview-grid" [class.reloading]="loading">

    <!-- 1: progress check -->
    <cs-card class="overview-card progress-check">
      <cs-card-title>
        {{ LL.bodyCompOverview.progressCheck() }}
        @if (model.progress.status) {
          <cs-status-chip title-action class="progress-status"
            [tone]="statusTone(model.progress.status)"
            [label]="model.progress.statusLabel"
            [info]="model.progress.statusInfo">
          </cs-status-chip>
        }
      </cs-card-title>
      <cs-card-content>
        <div class="trend-text">{{ model.progress.trendText }}</div>
        @for (change of model.progress.changes; track change.color) {
          <ng-container *ngTemplateOutlet="measurementTemplate; context: { $implicit: change }"></ng-container>
        }
        @if (model.progress.footnote) {
          <cs-note>{{ model.progress.footnote }}</cs-note>
        }
      </cs-card-content>
    </cs-card>

    <!-- 2: weight response -->
    <cs-card class="overview-card weight-response">
      <cs-card-title>{{ LL.bodyCompOverview.weightResponse() }}</cs-card-title>
      <cs-card-content>
        @if (model.weightResponse.emptyText) {
          <div class="empty-text">{{ model.weightResponse.emptyText }}</div>
        } @else if (model.weightResponse.measurement) {
          <ng-container *ngTemplateOutlet="measurementTemplate; context: { $implicit: model.weightResponse.measurement }"></ng-container>
        }
        @if (model.weightResponse.atKcalText) {
          <div class="intake-line">{{ model.weightResponse.atKcalText }}</div>
          <cs-note>{{ model.weightResponse.intakeSourceText }}</cs-note>
        }
      </cs-card-content>
    </cs-card>

    <!-- 3: weight chart -->
    <cs-card class="overview-card weight-chart">
      <cs-card-title>
        {{ LL.common.measurements.weight() }}
        @if (model.weight.nowText) {
          <cs-note>{{ model.weight.nowText }}</cs-note>
        }
      </cs-card-title>
      <cs-card-content>
        @if (model.weight.emptyText) {
          <div class="empty-text">{{ model.weight.emptyText }}</div>
        } @else {
          <cs-trend-band-chart
            [points]="model.weight.chart.points"
            [band]="model.weight.chart.band"
            [firstDayIso]="model.weight.chart.firstDayIso"
            [lastDayIso]="model.weight.chart.lastDayIso"
            [color]="weightColor"
            [unitName]="model.weight.chart.unitName"
            [formatDate]="formatDate">
          </cs-trend-band-chart>
        }
      </cs-card-content>
    </cs-card>

    <!-- 4: energy intake -->
    <cs-card class="overview-card energy-intake">
      <cs-card-title>
        {{ LL.bodyCompOverview.energyIntake() }}
        @if (model.intake?.averageText) {
          <cs-note>{{ model.intake.averageText }}</cs-note>
        }
      </cs-card-title>
      <cs-card-content>
        @if (model.intake; as intake) {
          <div class="intake-chart">
            <div class="bars-area">
              @if (targetLinePercent(intake.targetKcal, intake.maxKcal); as targetPercent) {
                <div class="target-line" [style.bottom.%]="targetPercent"></div>
              }
              @for (day of intake.days; track day.dateIso) {
                <div class="bar-track" [class.unticked]="!day.ticked">
                  <div class="bar" [style.height.%]="barHeightPercent(day, intake.maxKcal)"></div>
                </div>
              }
            </div>
            <div class="labels-row">
              @for (day of intake.days; track day.dateIso) {
                <div class="day-label">{{ day.label }}</div>
              }
            </div>
          </div>
          <!-- the legend, as cs-progress-chart draws its own -->
          <div class="legend">
            <div class="legend-item">
              <span class="legend-dot logged"></span>
              <cs-label class="legend-label">{{ LL.bodyCompOverview.legendLogged() }}</cs-label>
            </div>
            @if (intake.targetKcal !== undefined) {
              <div class="legend-item">
                <span class="legend-dot target"></span>
                <cs-label class="legend-label">{{ LL.bodyCompOverview.legendTarget() }}</cs-label>
              </div>
            }
          </div>
          <cs-note>
            {{ intake.weekText }}@if (intake.daysText) {<span class="separator"> · </span>{{ intake.daysText }}}
          </cs-note>
        } @else {
          <div class="empty-text">{{ model.noCheckupText }}</div>
        }
      </cs-card-content>
    </cs-card>

    <!-- 5: skinfolds, one outlined measurement box per site and the mean -->
    @if (model.skinfolds; as skinfolds) {
      <cs-card class="overview-card skinfolds">
        <cs-card-title>
          {{ LL.common.measurements.skinfolds() }}
          @if (skinfolds.footnote) {
            <cs-note>{{ skinfolds.footnote }}</cs-note>
          }
        </cs-card-title>
        <cs-card-content>
          <div class="measurement-boxes">
            @for (row of skinfolds.rows; track row.label) {
              <div class="measurement-box">
                <ng-container *ngTemplateOutlet="measurementTemplate; context: { $implicit: row }"></ng-container>
              </div>
            }
          </div>
        </cs-card-content>
      </cs-card>
    }

    <!-- 6: waist -->
    @if (model.waist; as waist) {
      <cs-card class="overview-card waist">
        <cs-card-title>{{ LL.common.measurements.waist() }}</cs-card-title>
        <cs-card-content>
          <cs-trend-band-chart
            [points]="waist.chart.points"
            [band]="waist.chart.band"
            [firstDayIso]="waist.chart.firstDayIso"
            [lastDayIso]="waist.chart.lastDayIso"
            [color]="waistColor"
            [unitName]="waist.chart.unitName"
            [formatDate]="formatDate">
          </cs-trend-band-chart>
          <ng-container *ngTemplateOutlet="measurementTemplate; context: { $implicit: waist.measurement }"></ng-container>
        </cs-card-content>
      </cs-card>
    }

    @if (model.noFatReadingsText) {
      <cs-card class="overview-card no-fat-readings">
        <cs-card-title>{{ LL.common.measurements.skinfolds() }}</cs-card-title>
        <cs-card-content>
          <div class="empty-text">{{ model.noFatReadingsText }}</div>
        </cs-card-content>
      </cs-card>
    }

    <!-- 7: adherence, one outlined box per figure -->
    <cs-card class="overview-card adherence">
      <cs-card-title>{{ LL.bodyCompOverview.adherence() }}</cs-card-title>
      <cs-card-content>
        @if (model.adherence; as adherence) {
          <div class="measurement-boxes">
            @for (box of adherence.boxes; track box.kind) {
              <div class="measurement-box adherence-box" [ngClass]="box.kind">
                <div class="measurement-label">{{ box.label }}</div>
                @if (box.emptyText) {
                  <div class="empty-text">{{ box.emptyText }}</div>
                } @else {
                  <div class="measurement-value">
                    <span class="current-value">{{ box.valueText }}</span>
                    <span class="value-suffix">{{ box.suffixText }}</span>
                  </div>
                }
                @if (box.percent; as percent) {
                  <cs-status-chip class="adherence-percent" [tone]="adherenceTone(percent.tone)" [label]="percent.text"></cs-status-chip>
                }
              </div>
            }
          </div>
        } @else {
          <div class="empty-text">{{ model.noCheckupText }}</div>
        }
      </cs-card-content>
    </cs-card>

  </div>
  @if (hasTrendDirections) {
    <cs-trend-legend class="overview-trend-legend"></cs-trend-legend>
  }
}
